import { ApplicationRef, inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, first, map, switchMap, tap } from 'rxjs/operators';
import { SsApiService } from './api/ss-api.service';
import { PlatformService } from 'ngx-unificator/services';
import { WA_WINDOW } from 'ngx-unificator/tokens';
import { ScriptLoaderService } from './script-loader.service';

export type RecaptchaVersion = 2 | 3;

export type RecaptchaSettings = {
  recaptcha: string;
  recaptcha_version: RecaptchaVersion;
};

export type CaptchaRequiredError = {
  errors: Record<string, any>;
  required: string[];
};

const RECAPTCHA_V3_SCRIPT = 'https://www.google.com/recaptcha/api.js?render=';
const RECAPTCHA_V2_SCRIPT = 'https://www.google.com/recaptcha/api.js';
const RECAPTCHA_V2_CONTAINER_ID = 'recaptcha-v2-container';

declare const grecaptcha: any;

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private _api = inject(SsApiService);
  private _document = inject(DOCUMENT);
  private _platform = inject(PlatformService);
  private _scriptLoader = inject(ScriptLoaderService);
  private _applicationRef = inject(ApplicationRef);
  private _window = inject<Window>(WA_WINDOW);

  private _settings$ = new BehaviorSubject<RecaptchaSettings | null>(null);
  private _scriptReady$ = new BehaviorSubject<boolean>(false);
  private _v2WidgetId: number | null = null;

  initCaptcha(): Observable<RecaptchaSettings | null> {
    if (!this._platform.isBrowser) return EMPTY;

    return this._api.playerSettings().pipe(
      map((response: any) => {
        const settings: RecaptchaSettings = {
          recaptcha: response?.recaptcha,
          recaptcha_version: response?.recaptcha_version,
        };
        return settings;
      }),
      tap((settings) => {
        if (settings?.recaptcha && settings?.recaptcha_version) {
          this._settings$.next(settings);
          this._loadScript(settings);
        }
      }),
      catchError(() => of(null)),
    );
  }

  get version(): RecaptchaVersion | null {
    return this._settings$.value?.recaptcha_version ?? null;
  }

  get siteKey(): string | null {
    return this._settings$.value?.recaptcha ?? null;
  }

  /**
   * - v3: invisible execution with a given action label.
   * - v2: renders a challenge widget inside the element matching
   *   `RECAPTCHA_V2_CONTAINER_ID` and waits for the user to solve it.
   * - disabled: emits `null` immediately so the default flow is never blocked.
   *
   * @param action  Used only for v3 (e.g. 'sign_in', 'sign_up').
   */
  execute(action = 'submit'): Observable<string | null> {
    const settings = this._settings$.value;

    if (!settings?.recaptcha || !settings?.recaptcha_version) {
      return of(null);
    }

    return this._scriptReady$.pipe(
      filter(Boolean),
        first(),
      switchMap(() => (settings.recaptcha_version === 3 ? this._executeV3(settings.recaptcha, action) : this._executeV2(settings.recaptcha))),
    );
  }

  isCaptchaRequired(error: any): boolean {
    const body: CaptchaRequiredError = error?.error ?? error;
    return Array.isArray(body?.required) && body.required.includes('captcha');
  }

  resetV2(): void {
    if (this._v2WidgetId !== null && typeof grecaptcha !== 'undefined') {
      grecaptcha.reset(this._v2WidgetId);
    }
  }

  private _executeV3(siteKey: string, action: string): Observable<string> {
    return new Observable<string>((observer) => {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(siteKey, { action })
          .then((token: string) => {
            observer.next(token);
            observer.complete();
          })
          .catch((err: unknown) => {
            observer.error(err);
          });
      });
    });
  }

  private _executeV2(siteKey: string): Observable<string> {
    return new Observable<string>((observer) => {
      const container = this._document.getElementById(RECAPTCHA_V2_CONTAINER_ID);

      if (!container) {
        observer.error(new Error(`reCAPTCHA v2 requires a DOM element with id="${RECAPTCHA_V2_CONTAINER_ID}".`));
        return;
      }

      container.innerHTML = '';
      this._v2WidgetId = null;

      this._v2WidgetId = grecaptcha.render(container, {
        'sitekey': siteKey,
        'callback': (token: string) => {
          observer.next(token);
          observer.complete();
        },
        'error-callback': () => {
          observer.error(new Error('reCAPTCHA v2 error.'));
        },
        'expired-callback': () => {
          observer.error(new Error('reCAPTCHA v2 token expired.'));
        },
      });
    });
  }

  private _loadScript(settings: RecaptchaSettings): void {
    this._applicationRef.isStable.pipe(filter(Boolean), first()).subscribe(() => this._scheduleRecaptchaLoad(settings));
  }

  private _scheduleRecaptchaLoad(settings: RecaptchaSettings): void {
    const load = () => this._loadRecaptcha(settings);

    this._window.setTimeout(() => {
      if (typeof this._window.requestIdleCallback === 'function') {
        this._window.requestIdleCallback(load, { timeout: 5_000 });
      } else {
        load();
      }
    }, 3_500);
  }

  private _loadRecaptcha(settings: RecaptchaSettings): void {
    const url = settings.recaptcha_version === 3 ? `${RECAPTCHA_V3_SCRIPT}${settings.recaptcha}` : RECAPTCHA_V2_SCRIPT;

    this._scriptLoader
      .load(url)
      .pipe(
        catchError(() => {
          console.error('Failed to load reCAPTCHA script.');
          return of(null);
        }),
      )
      .subscribe((event) => {
        if (event) {
          this._scriptReady$.next(true);
        }
      });
  }
}
